import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, useDatabaseData } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Combobox } from "@/components/ui/combobox"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { zodResolver } from "@hookform/resolvers/zod"
import { Send, Plus, Trash2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { languages } from "@/lib/agents/languages"
import { supabase } from "@/lib/data"
import { useMemo, useState, useEffect } from "react"

const languageOptions = languages.flatMap(l => "code" in l ? [{ value: l.code, label: l.label }] : [])

const timezoneOptions = (((Intl as any).supportedValuesOf?.("timeZone") ?? []) as string[]).map(tz => ({ value: tz, label: tz }))
const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export const campaigns_crud = {
  title: "Campaign",
  route: "/campaign/:id",
  component,
  resource: "jobs",
  icon: Send,
  action: "create",
} satisfies PageConfig

const FormSchema = z.object({
  title: z.string(),
  template: z.string(),
  language: z.string(),
  channel: z.string().min(1, "Required"),
  timezone: z.string().min(1, "Required"),
  type: z.number(),
  range: z.number(),
  day: z.number().optional().nullable(),
  sun: z.boolean().optional().nullable(),
  mon: z.boolean().optional().nullable(),
  tue: z.boolean().optional().nullable(),
  wed: z.boolean().optional().nullable(),
  thu: z.boolean().optional().nullable(),
  fri: z.boolean().optional().nullable(),
  sat: z.boolean().optional().nullable(),
}).refine((d) => d.type !== 2 || (d.day != null && d.day >= 1 && d.day <= 31), {
  message: "Day of month is required",
  path: ["day"],
}).refine((d) => d.type !== 1 || [d.sun, d.mon, d.tue, d.wed, d.thu, d.fri, d.sat].some(Boolean), {
  message: "Select at least one day",
  path: ["sun"],
})

type FormData = z.infer<typeof FormSchema>

type ComponentField = { name: string; value: string }

const typeOptions = [
  { value: 0, label: "Hourly" },
  { value: 1, label: "Daily" },
  { value: 2, label: "Monthly" },
]

const rangeOptions = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00 to ${String((i + 1) % 24).padStart(2, '0')}:00`,
}))

const daysOfWeek = [
  { key: "sun", label: "Sunday" },
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
]

export function component() {
  const {
    data,
    loading,
    submit,
    toastSuccess,
    navigate,
  } = useDatabaseItem("jobs")

  const { selectedTeam } = useTeamStore()
  const { data: contacts, loading: contactsLoading } = useDatabaseData("contacts", {
    filter: (supa) => supa.eq("team", selectedTeam?.id).eq("type", "user"),
  })
  const { data: channels, loading: channelsLoading } = useDatabaseData("channels", {
    filter: (supa) => supa.eq("team", selectedTeam?.id).eq("type", "whatsapp"),
  })

  const [allTeam, setAllTeam] = useState(true)
  const [contactIds, setContactIds] = useState<string[]>([])
  const [contactSearch, setContactSearch] = useState("")
  const [components, setComponents] = useState<ComponentField[]>([])
  const [areaCode, setAreaCode] = useState("")
  const [lastHeardDays, setLastHeardDays] = useState("")
  const [teamMatchCount, setTeamMatchCount] = useState<number | null>(null)
  const [countLoading, setCountLoading] = useState(false)
  const [listReady, setListReady] = useState(false)

  // Defer mounting the (potentially large) contacts list so toggling the switch stays snappy.
  useEffect(() => {
    if (allTeam) return
    setListReady(false)
    const t = setTimeout(() => setListReady(true), 50)
    return () => clearTimeout(t)
  }, [allTeam])

  useEffect(() => {
    if (data?.parameters) {
      const params = typeof data.parameters === 'string' ? JSON.parse(data.parameters) : data.parameters
      if (params && typeof params === 'object') {
        setAllTeam(params.all_team ?? true)
        setContactIds(Array.isArray(params.contact_ids) ? params.contact_ids : [])
        setComponents(Array.isArray(params.components) ? params.components : [])
        setAreaCode(params.area_code ?? "")
        setLastHeardDays(params.last_heard_days ? String(params.last_heard_days) : "")
      }
    }
  }, [data])

  // Null when the filter is off. Kept as a Date so the query and the client-side count agree on the instant.
  const cutoff = useMemo(() => {
    const days = Number(lastHeardDays) || 0
    return days > 0 ? new Date(Date.now() - days * 86400000) : null
  }, [lastHeardDays])

  // All-team mode counts on the server: exact, and not capped by the row limit of the contacts list.
  useEffect(() => {
    if (!cutoff || !allTeam || !selectedTeam?.id) {
      setTeamMatchCount(null)
      setCountLoading(false)
      return
    }
    setCountLoading(true)
    const t = setTimeout(async () => {
      const { count } = await supabase.from("contacts").select("id", { count: "exact", head: true })
        .eq("team", selectedTeam.id).eq("type", "user").gte("last_inbound_at", cutoff.toISOString())
      setTeamMatchCount(count ?? 0)
      setCountLoading(false)
    }, 400)
    return () => clearTimeout(t)
  }, [cutoff, allTeam, selectedTeam?.id])

  const selectedMatchCount = useMemo(() => {
    if (!cutoff || !contacts) return null
    const selected = new Set(contactIds)
    return contacts.filter(c => selected.has(c.id) && c.last_inbound_at && new Date(c.last_inbound_at).getTime() >= cutoff.getTime()).length
  }, [cutoff, contactIds, contacts])

  const formValues = useMemo(() => {
    const params = data?.parameters && typeof data.parameters === 'object' ? data.parameters as any : {}
    return { ...data, template: params.template ?? "", language: params.language ?? "en", channel: params.channel ?? "", timezone: params.timezone ?? defaultTimezone }
  }, [data])

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: formValues,
    values: formValues as FormData,
  })

  const selectedType = form.watch("type")

  const filteredContacts = useMemo(() => {
    if (!contacts) return []
    const q = contactSearch.trim().toLowerCase()
    if (!q) return contacts
    return contacts.filter(c => (c.name || c.id).toLowerCase().includes(q))
  }, [contacts, contactSearch])

  const toggleContact = (id: string) => {
    setContactIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])
  }

  const addComponent = () => setComponents([...components, { name: "", value: "" }])
  const removeComponent = (index: number) => setComponents(components.filter((_, i) => i !== index))
  const updateComponent = (index: number, field: keyof ComponentField, value: string) => {
    const next = [...components]
    next[index] = { ...next[index], [field]: value }
    setComponents(next)
  }

  async function onSubmit(data: FormData) {
    const { template, language, channel, timezone, ...scheduleFields } = data

    const parameters = {
      kind: "whatsapp_template",
      channel,
      template,
      language,
      timezone,
      components: components.filter(c => c.name),
      all_team: allTeam,
      contact_ids: allTeam ? [] : contactIds,
      area_code: allTeam ? areaCode.replace(/\D/g, "") : "",
      last_heard_days: Number(lastHeardDays) || undefined,
    }

    const submitData = {
      ...scheduleFields,
      team: selectedTeam?.id,
      agent: null,
      parameters,
    }

    await submit(submitData)
    toastSuccess()
    navigate("/campaigns")
  }

  const channelOptions = (channels || []).map(c => ({ value: c.id, label: c.title || c.id }))

  if (loading || channelsLoading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="jobs" onSuccess={"/campaigns"} />
          </PageTitle>

          <div className="rgrid">

            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="rc-3">
                <FormField control={form.control} name="template" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template name</FormLabel>
                    <FormControl><Input {...field} placeholder="approved_template_name" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="rc-3">
                <FormField control={form.control} name="language" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Language</FormLabel>
                    <FormControl>
                      <Combobox items={languageOptions} value={field.value} onChange={field.onChange} placeholder="Select a language..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="channel" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channel</FormLabel>
                    <FormControl>
                      <Combobox items={channelOptions} value={field.value} onChange={field.onChange} placeholder="Select a WhatsApp channel..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-3">
                <FormField control={form.control} name="type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {typeOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-3">
                <FormField control={form.control} name="range" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Range</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {rangeOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="timezone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time Zone</FormLabel>
                    <FormControl>
                      <Combobox items={timezoneOptions} value={field.value} onChange={field.onChange} placeholder="Select a time zone..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            {selectedType === 1 && (
              <div className="row">
                <div className="rc-12">
                  <FormLabel>Days of Week</FormLabel>
                  <div className="flex gap-2 mt-2">
                    {daysOfWeek.map((day) => (
                      <FormField key={day.key} control={form.control} name={day.key as keyof FormData} render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Button type="button" variant={field.value ? "default" : "outline"} onClick={() => field.onChange(!field.value)} className="w-full">
                              {day.label}
                            </Button>
                          </FormControl>
                        </FormItem>
                      )} />
                    ))}
                  </div>
                  {form.formState.errors.sun && <p className="text-sm font-medium text-destructive mt-2">{form.formState.errors.sun.message}</p>}
                </div>
              </div>
            )}

            {selectedType === 2 && (
              <div className="row">
                <div className="rc-2">
                  <FormField control={form.control} name="day" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Day of Month</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} max={31} {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            <div className="row">
              <div className="rc-12">
                <div className="flex items-center justify-between mb-4">
                  <FormLabel>Parameters</FormLabel>
                  <Button type="button" variant="outline" size="sm" onClick={addComponent}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Parameters
                  </Button>
                </div>

                {components.length === 0 && (
                  <div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-md">
                    No parameters defined. Click "Add Parameters" to create one.
                  </div>
                )}

                {components.map((item, index) => (
                  <div key={index} className="row mb-4 p-4 border rounded-md">
                    <div className="rc-4">
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input value={item.name} onChange={(e) => updateComponent(index, 'name', e.target.value)} placeholder="name" />
                        </FormControl>
                      </FormItem>
                    </div>
                    <div className="rc-7">
                      <FormItem>
                        <FormLabel>Value</FormLabel>
                        <FormControl>
                          <Input value={item.value} onChange={(e) => updateComponent(index, 'value', e.target.value)} placeholder="value" />
                        </FormControl>
                      </FormItem>
                    </div>
                    <div className="rc-1 flex items-end">
                      <Button type="button" variant="destructive" size="icon" onClick={() => removeComponent(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="row">
              <div className="rc-12">
                <div className="mb-4">
                  <FormLabel>Last heard from (optional)</FormLabel>
                  <Input value={lastHeardDays} onChange={(e) => setLastHeardDays(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 40" className="mt-2 max-w-40" />
                  <p className="text-sm text-muted-foreground mt-1">
                    Only contacts who sent a message within this many days will be included. Contacts we have never heard from are always excluded. Leave it empty to send regardless of activity.
                  </p>
                  {cutoff && <p className="text-sm mt-1">
                    {countLoading ? "Counting..." : allTeam
                      ? `${teamMatchCount ?? 0} contacts in this team wrote in the last ${lastHeardDays} days.`
                      : `${selectedMatchCount ?? 0} of ${contactIds.length} selected contacts wrote in the last ${lastHeardDays} days.`}
                    {allTeam && areaCode && " The area code filter is applied when the campaign runs and is not reflected here."}
                  </p>}
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <Switch checked={allTeam} onCheckedChange={setAllTeam} />
                  <FormLabel className="mb-0">Send to all contacts in team</FormLabel>
                </div>

                {allTeam && (
                  <div className="mb-2">
                    <FormLabel>Area code filter (optional)</FormLabel>
                    <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="e.g. 54 or +54 11" className="mt-2" />
                    <p className="text-sm text-muted-foreground mt-1">Only contacts whose phone number starts with this prefix (country, or country + city) will be included. Symbols and spaces are ignored.</p>
                  </div>
                )}

                {!allTeam && (
                  <>
                    <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search contacts..." className="mb-2" disabled={contactsLoading || !listReady} />
                    <div className="border rounded-md p-4 max-h-128 overflow-y-auto">
                      {(contactsLoading || !listReady) ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">Loading...</div>
                      ) : filteredContacts.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">No contacts found.</div>
                      ) : (
                        filteredContacts.map((contact) => (
                          <div key={contact.id} className="flex items-center gap-2 py-1">
                            <Checkbox checked={contactIds.includes(contact.id)} onCheckedChange={() => toggleContact(contact.id)} />
                            <span className="text-sm">{contact.name || contact.id}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </form>
      </Form>
    </div>
  </Page>
}
